// Date-range resolution for the enhanced Data Center forms (Result Samples,
// Historic Results) whose Query Panel offers BOTH a preset "Date Range" (the
// received-date shortcut) AND explicit Date Collected / Date Analyzed pickers.

/** The explicit From/To date fields a Query Panel can carry (empty string = unset). */
export interface QueryDateFields {
  receivedFrom?: string;
  receivedTo?: string;
  collectedFrom?: string;
  collectedTo?: string;
  analyzedFrom?: string;
  analyzedTo?: string;
}

/**
 * Resolves the Date Range that the query (grid AND column-filter dropdowns)
 * should actually apply.
 *
 * The preset `Date Range` chip is only a shortcut for the **Date Received**
 * lower bound. When the user instead searches by an explicit **Date Collected**
 * or **Date Analyzed** range (and leaves Date Received blank), that preset must
 * widen to "All" (`null`) — otherwise historical rows, which were *received*
 * long ago (e.g. the pre-Alpaca `BTLIMSDC` archive shown on Result Samples),
 * are silently excluded by the received-date cutoff even though they match the
 * collected/analyzed filter the user asked for.
 *
 * Returns `null` ("All") in that case, otherwise the given `range` unchanged.
 * An explicit Date Received range leaves the preset as-is (the backend's own
 * From/To already overrides it). Forms without collected/analyzed pickers never
 * set those fields, so they always get `range` back untouched.
 *
 * Fixes the "Result Samples do not show historical data before Alpaca LIMS"
 * ticket (2026-10-29).
 */
export function effectiveDateRange(
  range: number | null,
  d: QueryDateFields,
): number | null {
  const hasReceived = !!(d.receivedFrom || d.receivedTo);
  const hasCollectedOrAnalyzed = !!(
    d.collectedFrom ||
    d.collectedTo ||
    d.analyzedFrom ||
    d.analyzedTo
  );
  return hasCollectedOrAnalyzed && !hasReceived ? null : range;
}
